import React, { useState, useMemo, useEffect, useCallback, useContext, createContext } from 'react';
import { Spin, Tooltip, Popover } from 'antd';
import { capitalize, ceil, debounce, trim } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { AiOutlineDelete } from 'react-icons/ai';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';

import SideIcon from "../../../../assets/icons/side-bar-icon-blue.svg";
import WarningIcon from '../../../../assets/icons/warning-modal-icon.svg';
import DownloadICon from "../../../../assets/icons/download-icon-drop.svg"

import SearchInput from "../../../../components/uiComponent/inputs/searchinput/index";
import Button from "../../../../components/uiComponent/button/index";
import ReactSelect from "../../../../components/uiComponent/select/index"
import Pagination from "../../../../components/pagination/index";
import AgGrid from "../../../../components/ag-grid-table/index";
import Badge from '../../../../components/uiComponent/bages';
import Modal from '../../../../components/uiComponent/modal/index';
import ProgressBar from '../../../../components/uiComponent/progressBar';
import CustomTabs from '../../../../components/uiComponent/customTabs';

import { DownloadPickList } from '../../../../slices/post-shipment';
import { generateExcel, generatePDF } from '../../../../utils/download-file';

import { ModalWrapper } from '../../../purchase-order/po-warehouse-details/custom-drawer/style';
import { WorkOrderWrapper } from './style';
import { DeleteWorkOrder, GetUserWorkOrders, removeDataEntry, updateState, CreateWorkOrder, MergeWorkOrders } from '../../../../slices/work-orders';
import RowData from './rowData';
import MergeWorkOrdersModal from './MergeWorkOrdersModal';

import { convertUtcToEst } from '../../../../utils/helpers';

// const statuses = [
//   { value: 'Draft', label: 'Draft' },
//   { value: 'Processed', label: 'Processed' },
//   { value: 'Confirmed', label: 'Confirmed' },
//   { value: 'In Progress', label: 'In Progress'},
//   { value: 'Done', label: 'Done'}
// ];



const defaultColDef = {
  resizable: true,
  sortable: false,
  filter: false,
  suppressRowClickSelection: true,
  suppressMenu: true,
};

const DropdownStyledWrapper = styled.div`
.menu-wrapper-id {
  p {
    margin: 0px;
    padding: 4px 12px;
    font-weight: 400;
    font-size: 10px;
    line-height: 12px;
    vertical-align: middle;
    color: #4E5969;
    &:hover{
      background: #F5F5F5;
      cursor: pointer;
    }
}
}
`;

let gridColumnApi

const WorkOrderSelectionContext = createContext({
  selectedIds: new Set(),
  toggle: () => {},
  selectAllVisibleDrafts: () => {},
  deselectVisibleDrafts: () => {},
});

const SelectCheckboxCell = ({ data }) => {
  const { selectedIds, toggle } = useContext(WorkOrderSelectionContext);
  const isDraft = data?.status === 'Draft';
  const checked = data ? selectedIds.has(data.id) : false;
  return (
    <input
      type="checkbox"
      disabled={!isDraft}
      checked={checked}
      onChange={() => isDraft && toggle(data)}
      style={{
        cursor: isDraft ? 'pointer' : 'not-allowed',
        opacity: isDraft ? 1 : 0.5,
      }}
    />
  );
};

const SelectHeaderCheckbox = (params) => {
  const { selectedIds, selectAllVisibleDrafts, deselectVisibleDrafts } = useContext(WorkOrderSelectionContext);
  const [allDraftSelected, setAllDraftSelected] = useState(false);

  useEffect(() => {
    const refresh = () => {
      const draftIds = [];
      params.api.forEachNode((n) => {
        if (n.data?.status === 'Draft') draftIds.push(n.data.id);
      });
      setAllDraftSelected(
        draftIds.length > 0 && draftIds.every((id) => selectedIds.has(id))
      );
    };
    params.api.addEventListener('modelUpdated', refresh);
    refresh();
    return () => {
      params.api.removeEventListener('modelUpdated', refresh);
    };
  }, [selectedIds, params.api]);

  return (
    <input
      type="checkbox"
      checked={allDraftSelected}
      onChange={(e) => {
        if (e.target.checked) selectAllVisibleDrafts(params.api);
        else deselectVisibleDrafts(params.api);
      }}
      style={{ cursor: 'pointer' }}
    />
  );
};

const WorkOrder = () => {
  const [isDelete, setIsDelete] = useState(false);
  const [page, setPage] = useState({ pageSize: 25, pageNumber: 1 });
  const [filters, setFilters] = useState({ search: '', status: '' });
  const [workOrderData, setWorkOrderData] = useState({});
  const [sortBy, setSortBy] = useState({ key: '', type: '' });
  const [activeStatus, setActiveStatus] = useState(null); 
  const [showPopover, setShowPopover] = useState(false);
    const [downloadPickListLoading, setDownloadPickListLoading] = useState({});
  const [selectedWorkOrders, setSelectedWorkOrders] = useState([]);
  const [mergeModalOpen, setMergeModalOpen] = useState(false);

  const selectedIds = useMemo(
    () => new Set(selectedWorkOrders.map((w) => w.id)),
    [selectedWorkOrders]
  );

  const toggleSelection = useCallback((data) => {
    if (!data) return;
    setSelectedWorkOrders((prev) => {
      const exists = prev.some((w) => w.id === data.id);
      if (exists) return prev.filter((w) => w.id !== data.id);
      return [...prev, data];
    });
  }, []);

  const selectAllVisibleDrafts = useCallback((api) => {
    setSelectedWorkOrders((prev) => {
      const map = new Map(prev.map((w) => [w.id, w]));
      api?.forEachNode?.((n) => {
        if (n.data?.status === 'Draft') map.set(n.data.id, n.data);
      });
      return Array.from(map.values());
    });
  }, []);

  const deselectVisibleDrafts = useCallback((api) => {
    const visibleIds = new Set();
    api?.forEachNode?.((n) => {
      if (n.data?.status === 'Draft') visibleIds.add(n.data.id);
    });
    setSelectedWorkOrders((prev) => prev.filter((w) => !visibleIds.has(w.id)));
  }, []);

  const selectionContextValue = useMemo(
    () => ({ selectedIds, toggle: toggleSelection, selectAllVisibleDrafts, deselectVisibleDrafts }),
    [selectedIds, toggleSelection, selectAllVisibleDrafts, deselectVisibleDrafts]
  );

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const {
    userWorkOrders,
    fetchingUserWorkOrders,
    userWorkOrdersCount,
    deletingWorkOrder,
    creatingWorkOrder,
    userWorkOrderAllStatusCount,
    mergingWorkOrders
  } = useSelector((state) => state.workOrders || {});
  const { currentUser } = useSelector((state) => state.auth || {});

  const statuses = [
    {
      id: 'Draft',
      label: 'Draft',
      count: userWorkOrderAllStatusCount['Draft'] || 0,
      activeBg: '#7E8A90',
      inactiveBg: '#7E8A9033',
      activeText: '#fff',
      inactiveText: '#7E8A90',
      activeCountBg: '#fff',
      inactiveCountBg: '',
      activeCountText: '#fff',
      inactiveCountText: '#7E8A90',
      activeBorder: '#7E8A90',
      inactiveBorder: '#7E8A90',
      width: 182
    },
    {
      id: 'Confirmed',
      label: 'Confirmed',
      count: userWorkOrderAllStatusCount['Confirmed'] || 0,
      activeBg: '#1565D8',
      inactiveBg: '#1565D833',
      activeText: '#fff',
      inactiveText: '#1565D8',
      activeCountBg: '#fff',
      inactiveCountBg: '',
      activeCountText: '#fff',
      inactiveCountText: '#1565D8',
      activeBorder: '#1565D8',
      inactiveBorder: '#1565D8',
      width: 182
    },
    {
      id: 'In Progress',
      label: 'In Progress',
      count: userWorkOrderAllStatusCount['In Progress'] || 0,
      activeBg: '#FF9900',
      inactiveBg: '#FF99004D',
      activeText: '#fff',
      inactiveText: '#FF9900',
      activeCountBg: '#ffffff',
      inactiveCountBg: '',
      activeCountText: '#ffff',
      inactiveCountText: '#FF9900',
      activeBorder: '#FF9900',
      inactiveBorder: '#FF9900',
      width: 182
    },
    {
      id: 'Done',
      label: 'Done',
      count: userWorkOrderAllStatusCount['Done'] || 0,
      activeBg: '#009C34',
      inactiveBg: '#009C3433',
      activeText: '#fff',
      inactiveText: '#009C34',
      activeCountBg: '#ffffff',
      inactiveCountBg: '',
      activeCountText: '#fff',
      inactiveCountText: '#009C34',
      activeBorder: '#009C34',
      inactiveBorder: '#009C34',
      width: 182
    },
  ];

  useEffect(() => {
    dispatch(GetUserWorkOrders({ page, filters, sortBy }));
  }, []);

  const handleStatusFilter = (e) => {
    setFilters(filters => ({ ...filters, status: e?.value }));
    setPage(page => ({ ...page, pageNumber: 1 }));
    dispatch(GetUserWorkOrders({
      page: { ...page, pageNumber: 1 },
      filters: { ...filters, status: e?.value },
      sortBy
    }));
  };

  const isDraftTab = activeStatus === 'Draft';
  const columnDefs = useMemo(() => [
    {
      headerName: '',
      width: 48,
      maxWidth: 48,
      pinned: 'left',
      suppressMenu: true,
      sortable: false,
      filter: false,
      headerComponentFramework: SelectHeaderCheckbox,
      cellRendererFramework: SelectCheckboxCell,
    },
    {
      headerName: "",
      width: 75,
      cellRenderer: "agGroupCellRenderer",
      valueGetter: (params) => params.data?.shipments?.length || 0,
      suppressRowClickSelection: true,
    },
    {
      headerName: 'Created Date',
      headerClass: 'parent-header',
      sortable: false,
      filter: true,
      minWidth: 100,
      flex: 1,
      field: 'created_at',
      cellRenderer: ({ value }) => {
        const date = convertUtcToEst(value, {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        });

        const time = convertUtcToEst(value, {
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });

        return (
          <div className='date-item-wrapper'>
            <span>{date}</span>
            <span>{time}</span>
          </div>
        );
      }
    },
    {
      headerName: 'Work Order Name',
      headerClass: 'parent-header',
      sortable: false,
      filter: true,
      minWidth: 214,
      headerClass: 'parent-header',
      field: 'order_name',
      flex: 1,
      cellRenderer: ({ value, data }) => {
        return (
          <div className='card-wrapper'>
            <span className='two-line-ellipsis'>
              {value 
                ? <a href={
                  (currentUser?.role?.id === 5 && data?.packing_method === 'pack_now') || ["CONFIRMED", 'IN PROGRESS', "DONE"].includes(data?.status?.toUpperCase())
                    ? currentUser?.role?.id === 5 
                      ? `/shipping/${data.id}` 
                      : `/confirm-placement/${data.id}` 
                    : data?.is_placement_options_saved 
                      ? `/placement-option/${data.id}` 
                      : `/inventory/work-order/${data.id}`}>{value}</a> 
                : '---'}
            </span>
          </div>
        )
      }
    },
    {
      headerName: 'Type',
      headerClass: 'parent-header',
      sortable: false,
      filter: true,
      minWidth: 100,
      flex: 1,
      field: 'order_type',
      cellRenderer: ({ value }) => {
        return (
          <span>{ (value && value?.toUpperCase() === 'SHIP TO FBA' ? 'FBA' : 'FBM') || '---'}</span>
        )
      }
    },
    {
      headerName: 'Sku count',
      headerClass: 'parent-header',
      sortable: true,
      comparator: () => 0,
      filter: true,
      minWidth: 100,
      field: 'skus_count',
      flex: 1,
      cellRenderer: ({ value }) => {
        return (
          <div className='card-wrapper gap-1'>
            <span>{value || 0}</span>
          </div>
        )
      },
    },
      // {
      //   headerName: 'Ship Quantity',
      //   headerClass: 'parent-header',
      //   sortable: true,
      //   comparator: () => 0,
      //   filter: true,
      //   minWidth: 130,
      //   flex: 1,
      //   field: 'reserved_qty',
      //   cellRenderer: ({ value }) => {
      //     return (
      //       <div className='card-wrapper gap-1'>
      //         <span>{value || 0}</span>
      //       </div>
      //     )
      //   },
      // },
    {
      headerName: 'Progress',
      headerClass: 'parent-header-stock',
      sortable: false,
      filter: true,
      headerClass: 'amazon-parent-header',
      minWidth: 120,
      flex: 1,
      cellRenderer: ({ data }) => {
        const { shipments = [] } = data || {};
        let shipmentTotal = 0;
        let shipmentCurrent = 0;

        shipments.forEach(({ shipment_items_info = [] }) => {
          const items = Array.isArray(shipment_items_info) ? shipment_items_info : JSON.parse(shipment_items_info || '[]');
          items?.forEach(item => {
            shipmentTotal += Number(item.quantity) || 0;
            shipmentCurrent += Number(item.boxed_quantity) || 0;
          });
        });

        const total = Math.max(shipmentTotal, Number(data?.items_count) || 0, Number(data?.reserved_qty) || 0);
        const current = data?.status?.toUpperCase() === 'DRAFT' ? 0 : Math.max(Number(data?.packed_qty) || 0, shipmentCurrent);
        let status ='grey';

        if (current === 0) {
          status = 'grey'
        }
        else if (total === current) status = 'success'
        else if (current < total) status = 'warning'
        else status = 'warning'
        return (
          <ProgressBar  simpleProgress Width={118} status={status} current={current} total={total} />
        )
      },
    },
    {
      headerName: 'Status',
      headerClass: 'parent-header',
      sortable: false,
      field: 'status',
      filter: true,
      minWidth: 100,
      flex: 1,
      cellRendererFramework: ({ value }) => {
        if (!value) return '—';
        const s = value.toLowerCase();
        const cls = s === 'draft' ? 'badge-draft' : s === 'confirmed' ? 'badge-confirmed' : s.includes('progress') ? 'badge-in-progress' : s === 'done' ? 'badge-done' : '';
        return <span className={`wo-status-badge ${cls}`}>{capitalize(value)}</span>;
      }
    },
    {
      headerName: 'Action',
      headerClass: 'parent-header',
      sortable: false,
      filter: true,
      minWidth: 100,
      flex: 1,
      cellRenderer: ({ data, node }) => {
        const isLoading = downloadPickListLoading[data?.id] || false;
        return (
          <Box display="flex" gap={1} alignItems="center">
            <Tooltip title='Work Order Details'>
              <img className='cursor-pointer action-icons' alt='Work Order Details' onClick={() => {
                  if ((currentUser?.role?.id === 5 && data?.packing_method === 'pack_now') || data?.status?.toUpperCase() === 'CONFIRMED' || data?.status?.toUpperCase() === 'IN PROGRESS' || data?.status?.toUpperCase() === 'DONE') {
                    currentUser?.role?.id === 5 ? navigate(`/shipping/${data.id}`, { replace: true }) : navigate(`/confirm-placement/${data.id}`)
                  } else if (data?.is_placement_options_saved) {
                    navigate(`/placement-option/${data.id}`)
                  } else {
                    navigate(`/inventory/work-order/${data.id}`)
                  }
              }} src={SideIcon} />
            </Tooltip>
            {
              currentUser.role.id === 5 && (
                <Popover
                  defaultOpen={false}
                  open={showPopover && data.status !== 'Draft'}
                  onOpenChange={(value) => setShowPopover(value)}
                  overlayClassName="file-down-loa-ded-wrapper"
                  content={
                    <DropdownStyledWrapper>
                      <div className="menu-wrapper-id">
                        <p
                          onClick={() => {
                            downloadPickList(data, "excel", data?.id);
                            setShowPopover(false);
                          }}
                        >
                          By Excel File
                        </p>
                        <p
                          onClick={() => {
                            downloadPickList(data, "pdf", data?.id);
                            setShowPopover(false);
                          }}
                        >
                          By PDF File
                        </p>
                      </div>
                    </DropdownStyledWrapper>
                  }
                  placement="bottom"
                >
                  <Tooltip
                    title={['confirmed', 'done', 'in progress']?.includes(data?.status?.toLowerCase()) ? 'Download Pick List' : 'Cannot Download While Work Order is in Processed State'}
                    mouseEnterDelay={0.5}
                  >
                    {isLoading ? (
                      <Spin size="small" />
                    ) : (
                      <img
                        className={`action-icons cursor-pointer`}
                        style={data.status === 'Draft' ? { pointerEvents: 'none', opacity: 0.5 } : {}}
                        alt='Download Pick List'
                        onClick={() => {
                          if (data.status.toLowerCase() !== 'draft') {
                            setShowPopover(true);
                          }
                        }}
                        src={DownloadICon}
                      />
                    )}
                  </Tooltip>
                </Popover>
              ) 
            }
            <Tooltip title = 'Delete Work Order'>
              <AiOutlineDelete
                style={(data?.status === 'Draft') ? {} : { pointerEvents: 'none', opacity: 0.5 }}
                onClick={() => {
                  setIsDelete(true);
                  setWorkOrderData(data);
                }}
                className='delete-icon action-icons'
              />
            </Tooltip>
          </Box>
        )
      }
    },
  ], [isDraftTab, currentUser, downloadPickListLoading, showPopover]);

  const gridReady = (params) => {
    gridColumnApi = params.columnApi
  };

  const handlePagination = (number, key) => {
    const value = Number(number);
    let newPage = {};

    if (key === 'pageSize') newPage = { pageNumber: 1, pageSize: value };
    else newPage = { ...page, pageNumber: value };

    setPage(newPage);
    dispatch(GetUserWorkOrders({
      page: newPage,
      filters,
      sortBy
    }));
  };

  const refreshGridClick = () => {
    if (gridColumnApi){
      gridColumnApi.applyColumnState({
        defaultState: { sort: null },
        applyOrder: true,
      });
      setSortBy({ key: '', type: '' })
    }

    dispatch(GetUserWorkOrders({
      page,
      filters,
    }));
  };

    const downloadPickList = (data, type, id) => {
      if (!['confirmed', 'done', 'in progress']?.includes(data?.status?.toLowerCase())) return;
      setShowPopover(false);
      setDownloadPickListLoading((prev) => ({ ...prev, [id]: true }));
  
      dispatch(DownloadPickList(data?.id)).then(({ payload }) => {
        if (payload?.success) {
          const { pickList = [], orderName } = payload;
          (type === 'excel' || type === 'both') && generateExcel({ pickList, orderName });
          (type === 'pdf' || type === 'both') && generatePDF({ pickList, orderName });
          setDownloadPickListLoading((prev) => ({ ...prev, [id]: false }));
        }
      });
    }

  const handleSearch = debounce((value) => {
    if (trim(value) || filters.search) {
      setFilters(filters => ({ ...filters, search: value }));
      setPage(page => ({ ...page, pageNumber: 1 }));
      dispatch(GetUserWorkOrders({
        page: { ...page, pageNumber: 1 },
        filters: { ...filters, search: value },
        sortBy
      }));
    }
  }, 800);

  const handleDeleteWorkOrder = () => {
    const workOrderId = workOrderData?.id;
    dispatch(DeleteWorkOrder(workOrderId))
      .then(({ payload }) => {
        const { success } = payload || {};
        if (success) {
          // Refetch in place — deleting a work order must not clear the applied column sort
          // (refreshGridClick is the refresh button's full reset, wrong here).
          dispatch(GetUserWorkOrders({ page, filters, sortBy }));
          setIsDelete(false);
          dispatch(
            removeDataEntry({
              field: "userWorkOrders",
              key: 'id',
              value: workOrderId,
            })
          );
          dispatch(
            updateState({
              key: 'userWorkOrdersCount',
              value: userWorkOrdersCount - 1,
            })
          );

        }
      });
  };

  const onSortChange = (params) => {
    const value = params.columnApi.getColumnState().find(s => s.sort != null)

    if (value && ['skus_count', 'reserved_qty'].includes(value?.colId)) {
      const { colId, sort } = value || {};
      setSortBy({ key: colId, type: sort });
      dispatch(GetUserWorkOrders({
        page,
        filters,
        sortBy: { key: colId, type: sort }
      }));
    }
  };

  const handleCreateWorkOrder = () => {
    dispatch(CreateWorkOrder()).then(({ payload }) => {
      const { success, workOrderId } = payload || {};
      if (success) navigate(`/inventory/work-order/${workOrderId}`);
    });
  };

  const { pageNumber, pageSize } = page || {};

  const boxData = [
    { label: 'Total Orders', value: userWorkOrdersCount || 0, className: 'box-purple', key: null },
    { label: 'Draft', value: userWorkOrderAllStatusCount?.['Draft'] || 0, className: 'box-gray', key: 'Draft' },
    { label: 'Confirmed', value: userWorkOrderAllStatusCount?.['Confirmed'] || 0, className: 'box-blue', key: 'Confirmed' },
    { label: 'In Progress', value: userWorkOrderAllStatusCount?.['In Progress'] || 0, className: 'box-orange', key: 'In Progress' },
    { label: 'Done', value: userWorkOrderAllStatusCount?.['Done'] || 0, className: 'box-green', key: 'Done' },
  ];

  const handleCardFilter = (key) => {
    setActiveStatus(key);
    handleStatusFilter({ key: 'status', value: key });
  };

  const MERGE_MIN = 2;
  const MERGE_MAX = 5;
  const mergeDisabled = selectedWorkOrders.length < MERGE_MIN || selectedWorkOrders.length > MERGE_MAX || mergingWorkOrders;

  const handleMergeConfirm = async (orderName) => {
    const { payload } = await dispatch(MergeWorkOrders({
      workOrderIds: selectedWorkOrders.map(w => w.id),
      orderName,
    }));
    if (payload?.success) {
      setMergeModalOpen(false);
      setSelectedWorkOrders([]);
      navigate(`/inventory/work-order/${payload.workOrderId}`);
    }
  };

  return (
    <WorkOrderWrapper>
      {/* Stat Cards */}
      <div className="box-row">
        {boxData.map((data, idx) => (
          <div
            key={idx}
            className={`sku-box ${data.className} ${activeStatus === data.key ? 'active-box' : ''}`}
            onClick={() => handleCardFilter(data.key)}
          >
            <p>{data.label}</p>
            <h4>{data.value}</h4>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div className="wo-toolbar">
        <div className="toolbar-left">
          <h5 className="toolbar-title">Work Orders</h5>
          <span className="toolbar-count">{userWorkOrdersCount || 0}</span>
        </div>
        <div className="toolbar-right">
          <SearchInput
            placeholder="Search by name, type, SKU..."
            className="toolbar-search"
            onChange={({ target }) => handleSearch(target.value)}
          />
          <Tooltip
            title={
              selectedWorkOrders.length < MERGE_MIN
                ? `Select ${MERGE_MIN}–${MERGE_MAX} Draft work orders to merge`
                : selectedWorkOrders.length > MERGE_MAX
                  ? `At most ${MERGE_MAX} work orders can be merged at once`
                  : ''
            }
          >
            <span>
              <Button
                text={mergingWorkOrders ? 'Merging...' : `Merge Work Orders${selectedWorkOrders.length ? ` (${selectedWorkOrders.length})` : ''}`}
                className="outlined mb-0"
                onClick={() => setMergeModalOpen(true)}
                disabled={mergeDisabled}
              />
            </span>
          </Tooltip>
          <Button
            text="Create Work Order"
            className="primary mb-0"
            onClick={handleCreateWorkOrder}
            disabled={creatingWorkOrder}
          />
        </div>
      </div>

      {/* Table */}
        <Spin tip='Loading...' spinning={fetchingUserWorkOrders || deletingWorkOrder || creatingWorkOrder}>
          <WorkOrderSelectionContext.Provider value={selectionContextValue}>
            <AgGrid
              gridReady={gridReady}
              user_referrals={userWorkOrders}
              defaultColDef={defaultColDef}
              columnDefinitions={columnDefs}
              rowHeight={52}
              headerHeight={34}
              height="220px"
              masterDetail={true}
              onSortChange={onSortChange}
              detailRowAutoHeight={true}
              detailCellRenderer={(props) => <RowData {...props} />}
              className="sku-table"
              getRowId={(params) => String(params?.data?.id)}
            />
          </WorkOrderSelectionContext.Provider>
          <Pagination
            total={userWorkOrdersCount}
            totalPages={ceil(userWorkOrdersCount / pageSize + 1)}
            pageNumber={pageNumber}
            pageLimit={pageSize}
            handleLimitChange={(e) => handlePagination(e, 'pageSize')}
            handlePageNumberChange={(e) => handlePagination(e, 'pageNumber')}
            handlePageChangeLeft={(e) => handlePagination(e, 'pageNumber')}
            handlePageChangeRight={(e) => handlePagination(e, 'pageNumber')}
            refreshGridClick={refreshGridClick}
          />
        </Spin>

        <Modal
          open={isDelete}
          onClose={() => setIsDelete(false)}
          onHide={() => setIsDelete(false)}
          closeText='Cancel'
          saveText={'Confirm'}
          header='Delete Confirmation'
          className='delete-confirmation-modal'
          closeButton={true}
          onSave={handleDeleteWorkOrder}
          buttonDisabled={deletingWorkOrder}
        >
          <ModalWrapper>
            <div className='delete-modal-wrapper'>
              <img src={WarningIcon} alt='WarningIcon' title='WarningIcon' />
              <Typography fontSize={24} component="h3" marginTop={3}>Warning</Typography>
              <p>Are you sure you want to delete this order (<b>Name: {workOrderData?.order_name}</b>)?</p>
            </div>
          </ModalWrapper>
        </Modal>

        <MergeWorkOrdersModal
          open={mergeModalOpen}
          onClose={() => setMergeModalOpen(false)}
          selectedWorkOrders={selectedWorkOrders}
          onConfirm={handleMergeConfirm}
          loading={mergingWorkOrders}
        />
    </WorkOrderWrapper>
  )
}

export default WorkOrder;
